import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import ListLayout from "../../../components/Layout/ListLayout";
import { Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Snackbar, Alert, TablePagination, Button, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ClassificationFilters from "../../../components/Admin/Classification/ClassificationFilters";
import ClassificationList from "../../../components/Admin/Classification/ClassificationList";
import ClassificationForm from "../../../components/Admin/Classification/ClassificationForm";
import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getListArchivalClassificationQueryOptions,
  getCreateArchivalClassificationMutationOptions,
  getEditArchivalClassificationMutationOptions,
  getDeleteArchivalClassificationMutationOptions,
  getEnableArchivalClassificationMutationOptions,
  getDisableArchivalClassificationMutationOptions,
  getListArchivalClassificationQueryKey
} from "../../../api/documentos/endpoints";
import type { ArchivalClassificationResumoDto, ArchivalClassificationDto } from "../../../api/documentos/model";
import { useGenerateDoczToken } from "../../../api/bpm/endpoints";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../auth";

const classificationSearchSchema = z.object({
  page: z.number().catch(1),
  size: z.number().catch(10),
  q: z.string().optional(),
});

function getApiErrorMessage(error: unknown, fallback: string) {
  const apiMessage = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
  const errorMessage = (error as { message?: string })?.message;
  return apiMessage || errorMessage || fallback;
}

function getClassificationErrorMessage(error: unknown, fallback: string, t: (key: string) => string) {
  const message = getApiErrorMessage(error, fallback);
  const normalizedMessage = message.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

  if (normalizedMessage.includes("nao pode ser inativada") && normalizedMessage.includes("itens vinculados")) {
    return t("admin.classification.errors.cannotDeactivateWithLinkedItems");
  }

  if (normalizedMessage.includes("nao pode ser excluida") && normalizedMessage.includes("esta ativa")) {
    return t("admin.classification.errors.cannotDeleteActive");
  }

  return message;
}

export const Route = createFileRoute('/__auth/admin/classification')({
  component: ClassificationComponent,
  validateSearch: (search) => classificationSearchSchema.parse(search),
  staticData: {
    title: "admin.classification.title",
  },
});

function ClassificationComponent() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = Route.useNavigate();
  const search = Route.useSearch();
  const { currentTenant, organizations } = useAuth();

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ArchivalClassificationDto | null>(null);
  const [itemToDelete, setItemToDelete] = useState<ArchivalClassificationResumoDto | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const showSnackbar = (message: string, severity: 'success' | 'error' = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const effectiveTenant = currentTenant || "default-domain";
  const classificationQueryKey = getListArchivalClassificationQueryKey(effectiveTenant);
  const refreshClassifications = () => {
    queryClient.invalidateQueries({ queryKey: classificationQueryKey });
  };

  const currentOrg = useMemo(
    () => organizations.find((org) => org.key === effectiveTenant || org.id === effectiveTenant),
    [organizations, effectiveTenant]
  );

  const isDocz = currentOrg?.gestaoPCTTDD === "DOCZ";

  const { data, isLoading, error } = useQuery({
    ...getListArchivalClassificationQueryOptions(effectiveTenant, {
      isPageable: true,
      q: search.q || undefined,
      page: (search.page || 1) - 1,
      size: search.size || 10
    } as any),
    staleTime: 30000,
  });

  const rows: ArchivalClassificationResumoDto[] = useMemo(() => {
    const content = (data as any)?.data || data;
    return (content as { content?: ArchivalClassificationResumoDto[] })?.content ?? (Array.isArray(content) ? content : []);
  }, [data]);

  const totalElements = useMemo(() => {
    const content = (data as any)?.data || data;
    return (content as { totalElements?: number })?.totalElements ?? rows.length;
  }, [data, rows.length]);

  const handlePageChange = (_: unknown, value: number) => {
    navigate({
      search: (prev) => ({ ...prev, page: value }),
      replace: true
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleFilter = (newFilters: { q?: string }) => {
    navigate({
      search: (prev) => ({
        ...prev,
        ...newFilters,
        page: 1
      }),
    });
  };

  const enableMut = useMutation({
    ...getEnableArchivalClassificationMutationOptions(),
    onSuccess: () => {
      refreshClassifications();
      showSnackbar(t("admin.classification.statusSuccess"));
    },
    onError: (error) => showSnackbar(getClassificationErrorMessage(error, t("admin.classification.statusError"), t), "error"),
  });

  const disableMut = useMutation({
    ...getDisableArchivalClassificationMutationOptions(),
    onSuccess: () => {
      refreshClassifications();
      showSnackbar(t("admin.classification.statusSuccess"));
    },
    onError: (error) => showSnackbar(getClassificationErrorMessage(error, t("admin.classification.statusError"), t), "error"),
  });

  const handleToggleEnable = (item: ArchivalClassificationResumoDto) => {
    if ((item as any).enabled) {
      disableMut.mutate({ tenant: effectiveTenant, archivalClassificationId: item.id! });
    } else {
      enableMut.mutate({ tenant: effectiveTenant, archivalClassificationId: item.id! });
    }
  };

  const deleteMutation = useMutation({
    ...getDeleteArchivalClassificationMutationOptions(),
    onSuccess: () => {
      refreshClassifications();
      showSnackbar(t("admin.classification.deletedSuccess"));
    },
    onError: (error) => showSnackbar(getClassificationErrorMessage(error, t("admin.classification.deletedError"), t), "error"),
  });

  const createMutation = useMutation({
    ...getCreateArchivalClassificationMutationOptions(),
    onSuccess: () => {
      refreshClassifications();
      showSnackbar(t("admin.classification.createdSuccess"));
      setIsDialogOpen(false);
    },
    onError: () => showSnackbar(t("admin.classification.savedError"), "error"),
  });

  const updateMutation = useMutation({
    ...getEditArchivalClassificationMutationOptions(),
    onSuccess: () => {
      refreshClassifications();
      showSnackbar(t("admin.classification.updatedSuccess"));
      setIsDialogOpen(false);
    },
    onError: () => showSnackbar(t("admin.classification.savedError"), "error"),
  });

  const generateTokenMutation = useGenerateDoczToken({
    mutation: {
      onSuccess: async (res) => {
        try {
          const token = res.data as unknown as string;
          window.open(`${window.location.origin}/api/smartbpm/smart-bpm/docz/${token}`, '_blank');
        } catch (e) {
          showSnackbar(t("admin.classification.doczTokenProcessError"), "error");
        }
      },
      onError: () => showSnackbar(t("admin.classification.doczTokenError"), "error"),
    }
  });

  const handleManageInDocz = () => {
    generateTokenMutation.mutate({ tenant: effectiveTenant });
  };

  return (
    <ListLayout
      title={t("admin.classification.title")}
      subtitle={t("admin.classification.subtitle")}
      filters={
        <ClassificationFilters
          onApply={handleFilter}
          isLoading={isLoading}
          initialValues={{ q: search.q }}
        />
      }
      isLoading={isLoading}
      isEmpty={!rows.length}
      error={error as Error}
      actions={
        <IconButton color="primary" onClick={() => { setSelectedItem(null); setIsDialogOpen(true); }} disabled={isDocz}>
          <AddIcon />
        </IconButton>
      }
    >
      <ClassificationList
        data={rows}
        isDocz={isDocz}
        onManageInDocz={handleManageInDocz}
        onEdit={(item) => { setSelectedItem(item as ArchivalClassificationDto); setIsDialogOpen(true); }}
        onToggleEnable={handleToggleEnable}
        onDelete={(item) => {
          setItemToDelete(item);
          setIsDeleteDialogOpen(true);
        }}
      />

      <TablePagination
        component="div"
        count={totalElements}
        page={(search.page || 1) - 1}
        onPageChange={(_, newPage) => handlePageChange(null, newPage + 1)}
        rowsPerPage={search.size || 10}
        onRowsPerPageChange={(e) => {
          const newSize = parseInt(e.target.value, 10);
          navigate({
            search: (prev) => ({ ...prev, size: newSize, page: 1 }),
            replace: true
          });
        }}
        labelRowsPerPage={t("common.rowsPerPage")}
        labelDisplayedRows={({ from, to, count }) => `${t("common.showing")} ${from}-${to} ${t("common.of")} ${count}`}
        rowsPerPageOptions={[5, 10, 25, 50]}
        sx={{
          mt: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      />

      <Dialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogContent sx={{ p: 0 }}>
          <ClassificationForm
            initialValues={selectedItem || undefined}
            onSubmit={(vals) => {
              if (selectedItem?.id) {
                updateMutation.mutate({ tenant: effectiveTenant, archivalClassificationId: selectedItem.id, data: vals });
              } else {
                createMutation.mutate({ tenant: effectiveTenant, data: vals });
              }
            }}
            onCancel={() => setIsDialogOpen(false)}
            isLoading={createMutation.isPending || updateMutation.isPending}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={isDeleteDialogOpen} onClose={() => setIsDeleteDialogOpen(false)}>
        <DialogTitle>{t("admin.classification.deleteTitle")}</DialogTitle>
        <DialogContent>
          <Typography>
            {t("admin.classification.deleteConfirm", {
              name: itemToDelete?.assunto || itemToDelete?.codigoClassificacao || "",
            })}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setIsDeleteDialogOpen(false)} color="inherit">
            {t("common.cancel")}
          </Button>
          <Button
            onClick={() => {
              if (itemToDelete?.id) {
                deleteMutation.mutate({ tenant: effectiveTenant, archivalClassificationId: itemToDelete.id });
              }
              setIsDeleteDialogOpen(false);
              setItemToDelete(null);
            }}
            variant="contained"
            color="error"
          >
            {t("common.delete")}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </ListLayout>
  );
}
